import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faCodeBranch,
  faCircleXmark,
  faArrowLeft,
  faInbox,
  faClipboardCheck,
  faChevronRight,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/stores/auth.store';
import { dashboardService } from '@/services/dashboard.service';
import { inspectionService } from '@/services/inspection.service';
import { ROUTES } from '@/routes/routes';

function useRequestCenterData() {
  const user = useAuthStore((s) => s.user);
  const isMSE = user?.roleSlug === 'master-service-engineer';

  const engineerQuery = useQuery({
    queryKey: ['dashboard-engineer'],
    queryFn: dashboardService.getEngineerDashboard,
    staleTime: 60_000,
    enabled: !isMSE,
  });

  const mseQuery = useQuery({
    queryKey: ['dashboard-master-engineer'],
    queryFn: dashboardService.getMasterEngineerDashboard,
    staleTime: 60_000,
    enabled: isMSE,
  });

  const counts = isMSE ? mseQuery.data?.requestCenter : engineerQuery.data?.requestCenter;
  const isLoading = isMSE ? mseQuery.isLoading : engineerQuery.isLoading;
  const isError = isMSE ? mseQuery.isError : engineerQuery.isError;

  return { counts, isLoading, isError };
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  isLoading?: boolean;
  emptyMessage: string;
  children: React.ReactNode;
}

function Section({ icon, title, count, isLoading = false, children, emptyMessage }: SectionProps) {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        {icon}
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <span
          className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            count > 0 ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
          }`}
        >
          {isLoading ? '…' : count}
        </span>
      </div>
      <div className="divide-y divide-border">
        {isLoading ? (
          <div className="flex items-center gap-2 px-5 py-4 text-sm text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin h-3.5 w-3.5" />
            Loading…
          </div>
        ) : count === 0 ? (
          <p className="px-5 py-4 text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

interface InspectionRowProps {
  id: string;
  inspectionNumber: string;
  subtitle: string;
  onClick: () => void;
}

function InspectionRow({ inspectionNumber, subtitle, onClick }: InspectionRowProps) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-muted/30 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-mono font-medium text-foreground">{inspectionNumber}</p>
        <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
      </div>
      <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 shrink-0 text-muted-foreground" />
    </button>
  );
}

export function RequestCenterPage() {
  const navigate = useNavigate();
  const { counts, isLoading: countsLoading, isError } = useRequestCenterData();
  const userId = useAuthStore((s) => s.user?.userId);

  const pendingTransfersQuery = useQuery({
    queryKey: ['pending-transfers'],
    queryFn: inspectionService.getPendingTransfers,
    staleTime: 30_000,
    enabled: !!userId,
  });

  const pendingTakeoversQuery = useQuery({
    queryKey: ['pending-takeovers'],
    queryFn: inspectionService.getPendingTakeovers,
    staleTime: 30_000,
    enabled: !!userId,
  });

  const total = counts
    ? counts.transferRequests +
      counts.takeoverRequests +
      counts.rejectedItems +
      counts.authorizationRequests
    : 0;

  const navToInspection = (id: string) => navigate(ROUTES.INSPECTION_DETAIL.replace(':id', id));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">Request Center</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {countsLoading
              ? 'Loading…'
              : `${total} action${total !== 1 ? 's' : ''} requiring your attention`}
          </p>
        </div>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load request data. Check your connection and refresh.
        </div>
      )}

      <div className="space-y-4">
        {/* New Inspection Assignments */}
        <Section
          icon={
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/10 text-blue-700">
              <FontAwesomeIcon icon={faClipboardCheck} className="h-3.5 w-3.5" />
            </div>
          }
          title="New Inspection Assignments"
          count={counts?.inspectionRequests ?? 0}
          isLoading={countsLoading}
          emptyMessage="No new inspection assignments."
        >
          <button
            onClick={() => navigate(ROUTES.MY_INSPECTIONS)}
            className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-muted/30 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {counts?.inspectionRequests} inspection{counts?.inspectionRequests !== 1 ? 's' : ''}{' '}
                waiting to be started
              </p>
              <p className="text-xs text-muted-foreground">Open My Inspections to view</p>
            </div>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="h-3 w-3 shrink-0 text-muted-foreground"
            />
          </button>
        </Section>

        {/* Transfer Requests */}
        <Section
          icon={
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/10 text-amber-700">
              <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3.5 w-3.5" />
            </div>
          }
          title="Transfer Requests"
          count={pendingTransfersQuery.data?.length ?? counts?.transferRequests ?? 0}
          isLoading={pendingTransfersQuery.isLoading}
          emptyMessage="No pending transfer offers."
        >
          {pendingTransfersQuery.data?.map((item) => (
            <InspectionRow
              key={item.id}
              id={item.id}
              inspectionNumber={item.inspectionNumber}
              subtitle={
                item.assignedEngineerName
                  ? `From ${item.assignedEngineerName}${item.pendingTransferReason ? ` · ${item.pendingTransferReason}` : ''}`
                  : (item.pendingTransferReason ?? 'Transfer offer')
              }
              onClick={() => navToInspection(item.id)}
            />
          ))}
        </Section>

        {/* Takeover Requests */}
        <Section
          icon={
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-500/10 text-orange-700">
              <FontAwesomeIcon icon={faCodeBranch} className="h-3.5 w-3.5" />
            </div>
          }
          title="Takeover Requests"
          count={pendingTakeoversQuery.data?.length ?? counts?.takeoverRequests ?? 0}
          isLoading={pendingTakeoversQuery.isLoading}
          emptyMessage="No pending takeover requests on your inspections."
        >
          {pendingTakeoversQuery.data?.map((item) => (
            <InspectionRow
              key={item.id}
              id={item.id}
              inspectionNumber={item.inspectionNumber}
              subtitle={
                item.takeoverRequesterName
                  ? `Requested by ${item.takeoverRequesterName}${item.pendingTakeoverReason ? ` · ${item.pendingTakeoverReason}` : ''}`
                  : (item.pendingTakeoverReason ?? 'Takeover request')
              }
              onClick={() => navToInspection(item.id)}
            />
          ))}
        </Section>

        {/* Rejected / Returned Items */}
        <Section
          icon={
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-500/10 text-red-700">
              <FontAwesomeIcon icon={faCircleXmark} className="h-3.5 w-3.5" />
            </div>
          }
          title="Rejected / Returned Items"
          count={counts?.rejectedItems ?? 0}
          isLoading={countsLoading}
          emptyMessage="No returned inspections."
        >
          <button
            onClick={() => navigate(ROUTES.MY_INSPECTIONS)}
            className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-muted/30 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {counts?.rejectedItems} inspection{counts?.rejectedItems !== 1 ? 's' : ''} returned
                by operations
              </p>
              <p className="text-xs text-muted-foreground">Open My Inspections → Returned tab</p>
            </div>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="h-3 w-3 shrink-0 text-muted-foreground"
            />
          </button>
        </Section>

        <div className="rounded-xl border border-dashed border-border bg-muted/30 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <FontAwesomeIcon icon={faInbox} className="h-3.5 w-3.5" />
            </div>
            <p className="text-sm text-muted-foreground">
              Pending Reviews and Customer Approvals — coming soon
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
